import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiClockCounterClockwiseLight, PiClockLight } from 'react-icons/pi';
import Button from './TailwindComponents/Button';
import { formatDateTime } from '../../utils/functions';

export interface RecentActivityEntry {
  id?: number | string;
  action?: string;
  details?: string | null;
  notes?: string | null;
  timestamp?: string | null;
  created_at?: string | null;
  user_first_name?: string | null;
  user_last_name?: string | null;
  user_name?: string | null;
}

interface RecentActivityCardProps {
  /** Async function returning either an array of entries or a normalized
   *  { entries: [...] } / { data: [...] } payload. Must accept (limit) so
   *  the card only fetches what it needs. */
  fetchEntries: (limit: number) => Promise<any>;
  /** Optional callback wired to a "View all" button — typically opens the
   *  full slide-in audit log panel for the resource. Hides the button if not
   *  provided. */
  onViewAll?: () => void;
  /** How many entries to show. Default 5. */
  limit?: number;
  /** Override the title (default: "Recent Activity"). */
  title?: string;
}

const humanize = (raw: string): string =>
  raw
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ');

const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  fetchEntries,
  onViewAll,
  limit = 5,
  title,
}) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<RecentActivityEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setIsLoading(true);
      try {
        const result = await fetchEntries(limit);
        // Accept several payload shapes: array, {entries: []}, {data: []}, {audit_log: []}
        const list: RecentActivityEntry[] =
          Array.isArray(result)
            ? result
            : result?.entries || result?.data || result?.audit_log || [];
        if (active) setEntries(list.slice(0, limit));
      } catch {
        if (active) setEntries([]);
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <PiClockCounterClockwiseLight className="h-4 w-4" />
          <span className="text-xs font-medium">{title || t('Recent Activity')}</span>
        </div>
        {onViewAll && entries.length > 0 && (
          <Button variant="outline" size="small" onClick={onViewAll}>
            {t('View all')}
          </Button>
        )}
      </div>
      <div className="px-5 py-3">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="h-3.5 w-3.5 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulseFade flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
                  <div className="h-2.5 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
                </div>
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
            {t('No activity yet.')}
          </p>
        ) : (
          <ul className="space-y-2.5">
            {entries.map((entry, idx) => {
              const ts = entry.timestamp || entry.created_at;
              const userName =
                (entry.user_name && entry.user_name.trim()) ||
                `${entry.user_first_name || ''} ${entry.user_last_name || ''}`.trim();
              const text =
                (entry.details && entry.details.trim()) ||
                (entry.notes && entry.notes.trim()) ||
                (entry.action ? humanize(entry.action) : '');
              return (
                <li key={entry.id ?? idx} className="flex items-start gap-2.5">
                  <PiClockLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{text}</p>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                      {userName ? `${userName} · ` : ''}
                      {ts ? formatDateTime(ts) : ''}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RecentActivityCard;
